import useTranslation from 'next-translate/useTranslation'
import { useRouter } from 'next/router'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import React from 'react'
import { SearchBar } from '@/app/common/components/SearchPage/SearchBar'
import { SearchResults } from '@/app/common/components/SearchPage/SearchResults'
import { SearchSuggestions } from '@/app/common/components/SearchPage/SearchSuggestions'
import { useQuery } from '@/app/common/graphql/hooks'
import * as tracking from '@/helpers/tracking'
import { useLanguageCode } from '@/helpers/useRegionCode'
import { useSearchParams } from '@/helpers/useSearchParams'
import {
  SearchPageQueryDocument,
  SearchPageQueryQuery,
  SearchPageQueryQueryVariables,
} from '@/types/gql/graphql'
import { CardsGridReferenceStoryblok } from '@/types/storyblok-component-types'
import { cn } from '@/utils/cn'

const fiveMinutesInMS = 5 * 60 * 1000

interface Props {
  suggestions: string[]
  categoriesBlok: CardsGridReferenceStoryblok | null
}

export function SearchPage({ suggestions, categoriesBlok }: Props) {
  const searchBarRef = useRef<HTMLInputElement>(null)
  const languageCode = useLanguageCode()
  const { t } = useTranslation()
  const searchTerm = (useSearchParams().q || '') as string
  const router = useRouter()

  const [searchTime, setSearchTime] = useState(new Date())
  const [prevSearchTerm, setPrevSearchTerm] = useState(searchTerm)
  if (searchTerm !== prevSearchTerm) {
    const now = Date.now()
    if (now - searchTime.getTime() > fiveMinutesInMS) {
      setSearchTime(new Date())
    }
    setPrevSearchTerm(searchTerm)
  }

  const { data: searchQueryResults, isFetching } = useQuery<
    SearchPageQueryQuery,
    SearchPageQueryQueryVariables
  >(SearchPageQueryDocument, {
    skip: !searchTerm,
    enableCaching: true,
    staleTime: fiveMinutesInMS,
    keepPreviousData: true,
    variables: {
      languageCode,
      searchProductArgs: {
        search: searchTerm,
        isPublished: true,
        paginationArgs: {
          limit: 30,
        },
        createdBefore: searchTime,
      },
      searchItemCategoryArgs: {
        search: searchTerm,
        createdBefore: searchTime,
      },
    },
  })

  useEffect(() => {
    if (!searchTerm) return

    tracking.trackEvent('search', {
      'search-source': 'global-product',
      'search-term': searchTerm,
    })
  }, [searchTerm])

  useLayoutEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        router.back()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [router])

  const onSearchTermChange = useCallback(
    (term: string) => {
      const query = { ...router.query, q: term || undefined }
      if (!query.q) {
        delete query.q
      }
      router.replace(
        {
          query,
        },
        undefined,
        { shallow: true },
      )
    },
    [router],
  )

  const handleSuggestionClick = useCallback(
    (term: string) => {
      onSearchTermChange(term)
      searchBarRef.current?.focus()
    },
    [onSearchTermChange, searchBarRef],
  )

  const showEmptyState =
    !searchTerm &&
    !isFetching &&
    !searchQueryResults?.searchProducts.nodes.length &&
    !searchQueryResults?.searchItemCategories.length

  return (
    <div className="container mx-auto min-h-screen flex flex-col">
      <div
        className={cn(
          'sticky top-0 bg-white z-10 flex items-center px-2 flex-shrink-0',
          showEmptyState ? 'py-3' : 'pt-3',
        )}
      >
        <SearchBar
          searchTerm={searchTerm}
          ref={searchBarRef}
          onSearchTermChange={onSearchTermChange}
        />
        <button className="px-2 py-2 font-medium" onClick={() => router.back()}>
          {t('common:cancel')}
        </button>
      </div>
      {showEmptyState ? (
        <div className="px-2 py-3">
          <SearchSuggestions
            suggestions={suggestions}
            onSuggestionClick={handleSuggestionClick}
            categoriesBlok={categoriesBlok}
          />
        </div>
      ) : (
        <div className="py-5">
          <SearchResults
            isFetching={isFetching}
            searchTerm={searchTerm}
            products={searchQueryResults?.searchProducts.nodes || []}
            categories={searchQueryResults?.searchItemCategories || []}
          />
        </div>
      )}
    </div>
  )
}
